import { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, BellRing, BellOff, CheckCheck, Phone, PhoneMissed, PhoneForwarded, Voicemail, MessageSquare, MessageCircle, AlertTriangle, Mail, Clock, X, Zap, FileText, FileX2, HardDrive, Send, CalendarClock, Video, CheckCircle2, Loader2 } from 'lucide-react';
import { channelForNotification } from '@easyflow/shared';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@easyflow/ui-frontend/components/ui/popover';
import { ScrollArea } from '@easyflow/ui-frontend/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { useDesktopNotifications } from '@/hooks/useDesktopNotifications';
import { useAuth } from '@/contexts/AuthContext';
import { useSocketOptional } from '@/contexts/SocketContext';
import { AUTH_STORAGE_KEYS } from '@/constants/auth';
import { apiClient } from '@/lib/api';
import type { TenantNotification, NotificationListData, NotificationUnreadCount, NotificationAcknowledgeData, NotificationResolveData } from '@easyflow/shared';

const AUTO_REMOVE_DELAY_MS = 15 * 60 * 1000; // 15 minutes after being read

// Root-level routes that must NOT be prefixed with the tenant slug. These
// are handled by App.tsx outside of `/:tenantSlug/*` (master FE, public
// flows, system pages).
const NON_TENANT_PATH_PREFIXES = [
  '/master',
  '/setup',
  '/booking',
  '/widget',
  '/widgets',
  '/public',
  '/api',
  '/health',
  '/system',
];

function startsWithSegment(url: string, prefix: string): boolean {
  return url === prefix || url.startsWith(`${prefix}/`);
}

// Defensive fallback for action URLs (Task #1148).
//
// Backend services now normalize + validate `action_url` server-side via
// `notification-url.ts` (`normalizeAndValidateActionUrl`) before insert, so
// every newly persisted row already contains a `/<slug>/...` path. This
// function is kept to handle:
//   1. Legacy notifications stored before the audit landed.
//   2. Edge cases where the backend could not resolve a tenant slug
//      (e.g. tenant deleted while notification was in-flight) and stored
//      the raw `/tasks` path.
//   3. Notifications synced from external systems / webhooks that bypass
//      `notificationService.create()`.
// Source of truth for the registry lives in
// `packages/backend/src/services/notifications/notification-url.ts` —
// keep `NON_TENANT_PATH_PREFIXES` below in sync with that file.
function normalizeActionUrl(rawUrl: string, tenantSlug?: string): string {
  if (!rawUrl) return '/';
  const url = rawUrl.trim();
  if (!url) return '/';
  // External URLs and well-known schemes: leave untouched.
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(url) || url.startsWith('mailto:') || url.startsWith('tel:')) {
    return url;
  }
  // Protocol-relative URL ("//host/path") — treat as external.
  if (url.startsWith('//')) return url;
  // Hash-only or query-only fragments: navigate within current page, no prefix.
  if (url.startsWith('#') || url.startsWith('?')) return url;
  if (!tenantSlug) return url;
  // Already absolute path beginning with a slash.
  if (url.startsWith('/')) {
    // Already tenant-scoped.
    if (startsWithSegment(url, `/${tenantSlug}`)) return url;
    // Root-level routes that must remain at root (master FE, public, system).
    for (const prefix of NON_TENANT_PATH_PREFIXES) {
      if (startsWithSegment(url, prefix)) return url;
    }
    // Backend services emit tenant-relative paths without the slug
    // (e.g. /tasks, /crm, /messaging) — make them tenant-scoped.
    return `/${tenantSlug}${url}`;
  }
  // Relative path without leading slash → assume tenant-scoped feature path.
  return `/${tenantSlug}/${url}`;
}


/** Task #3976 — reference_type values for operational notifications. */
const OPERATIONAL_REFERENCE_TYPES = new Set(['pos_transfer', 'hr_request', 'appointment']);

type Notification = TenantNotification;

function operationalStatus(n: Notification): 'unread' | 'read' | 'acknowledged' | 'resolved' | null {
  if (!n.reference_type || !OPERATIONAL_REFERENCE_TYPES.has(n.reference_type)) return null;
  if (n.resolved_at) return 'resolved';
  if (n.acknowledged_at) return 'acknowledged';
  if (n.read) return 'read';
  return 'unread';
}

const typeIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  incoming_calls: Phone,
  missed_call: PhoneMissed,
  missed_calls: PhoneMissed,
  voicemail: Voicemail,
  messaging: MessageSquare,
  message_received: MessageSquare,
  system_alerts: AlertTriangle,
  system_alert: AlertTriangle,
  call_transfer: PhoneForwarded,
  queue_timeout: Clock,
  email_received: Mail,
  crm_event: Zap,
  invoice_lifecycle: FileText,
  invoice_rejected: FileX2,
  storage_quota: HardDrive,
};

const typeColors: Record<string, string> = {
  incoming_calls: 'text-green-500',
  missed_call: 'text-red-500',
  missed_calls: 'text-red-500',
  voicemail: 'text-purple-500',
  messaging: 'text-blue-500',
  message_received: 'text-blue-500',
  system_alerts: 'text-amber-500',
  system_alert: 'text-amber-500',
  call_transfer: 'text-indigo-500',
  queue_timeout: 'text-orange-500',
  email_received: 'text-teal-500',
  crm_event: 'text-orange-500',
  invoice_lifecycle: 'text-indigo-500',
  storage_quota: 'text-red-500',
  invoice_rejected: 'text-red-500',
};

const priorityStyles: Record<string, string> = {
  low: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  normal: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
  high: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
  urgent: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
};

export default function NotificationBell() {
  const { token: authToken } = useAuth();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const navigate = useNavigate();
  const socketContext = useSocketOptional();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const desktopNotifs = useDesktopNotifications();

  const handleToggleDesktop = useCallback(async () => {
    const result = await desktopNotifs.toggleNotifications();
    switch (result.status) {
      case 'enabled':
        toast({ title: 'Notifiche desktop attive', description: 'Riceverai gli avvisi anche quando la scheda è in background.' });
        break;
      case 'disabled':
        toast({ title: 'Notifiche desktop disattivate', description: 'Non riceverai più le push su questo dispositivo.' });
        break;
      case 'blocked':
        toast({
          title: 'Notifiche bloccate dal browser',
          description: 'Sblocca le notifiche dall\'icona accanto all\'URL (lucchetto / Informazioni sito) → Notifiche → Consenti, poi ricarica la pagina.',
          variant: 'destructive',
        });
        break;
      case 'unsupported':
        toast({ title: 'Notifiche non supportate', description: 'Questo browser non supporta le notifiche desktop.', variant: 'destructive' });
        break;
      case 'error':
        toast({ title: 'Errore notifiche', description: result.message || 'Si è verificato un errore imprevisto.', variant: 'destructive' });
        break;
    }
  }, [desktopNotifs, toast]);

  const token = authToken || localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);

  // Le chiamate passano da `apiClient` (prefisso tenant dal token, Bearer,
  // refresh single-flight su 401): `apiBase` resta solo come guardia di
  // montaggio insieme al token.
  const apiBase = tenantSlug ? `/api/${tenantSlug}` : '/api';

  const tokenRef = useRef(token);
  tokenRef.current = token;

  const fetchUnreadCount = useCallback(async () => {
    if (!tokenRef.current) return;
    try {
      const res = await apiClient.get<NotificationUnreadCount>('/notifications/unread-count');
      setUnreadCount(res.data?.count || 0);
    } catch (err) {
      if ((err as { status?: number }).status === 429) {
        console.warn('Rate limited on notifications, backing off');
        return;
      }
      console.error('Error fetching unread count:', err);
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    if (!tokenRef.current) return;
    setLoading(true);
    try {
      const res = await apiClient.get<NotificationListData>('/notifications?limit=10');
      setNotifications(res.data?.notifications || []);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!token || !apiBase) return;
    fetchUnreadCount();
    const isSocketUp = socketContext?.isConnected;
    const fallbackMs = isSocketUp ? 300000 : 45000;
    const interval = setInterval(fetchUnreadCount, fallbackMs);
    return () => clearInterval(interval);
  }, [tenantSlug, fetchUnreadCount, socketContext?.isConnected, apiBase, token]);

  // Use shared socket from SocketContext for real-time updates (rooms are auto-joined by backend)
  useEffect(() => {
    if (!socketContext?.socket || !socketContext?.isConnected) return;
    
    const socket = socketContext.socket;
    console.log('[NotificationBell] Using shared socket for notifications');
    
    const handleNotificationNew = (data: { type: string; title: string; body: string }) => {
      console.log('[NotificationBell] New notification received:', data);
      setUnreadCount(prev => prev + 1);
      if (open) {
        fetchNotifications();
      }
    };

    const handleNotificationCount = (data: { count: number }) => {
      if (typeof data?.count === 'number') {
        setUnreadCount(data.count);
      }
    };

    const handleNotificationRead = () => {
      setUnreadCount(prev => Math.max(0, prev - 1));
    };

    // Task #3976 — aggiornamenti di stato real-time (acknowledged_at / resolved_at).
    const handleStateUpdate = (data: { id?: string; acknowledged_at?: string | null; resolved_at?: string | null; dedupeKey?: string }) => {
      console.log('[NotificationBell] notification:state_update received:', data);
      if (data.id) {
        setNotifications(prev => prev.map(n => {
          if (n.id !== data.id) return n;
          return {
            ...n,
            ...(data.acknowledged_at !== undefined ? { acknowledged_at: data.acknowledged_at ?? null } : {}),
            ...(data.resolved_at !== undefined ? { resolved_at: data.resolved_at ?? null } : {}),
          };
        }));
        // Se la notifica è stata risolta, decrementa il contatore se non era già letta.
        if (data.resolved_at) {
          setNotifications(prev => {
            const n = prev.find(x => x.id === data.id);
            if (n && !n.read && !n.resolved_at) {
              setUnreadCount(c => Math.max(0, c - 1));
            }
            return prev;
          });
        }
      } else if (data.dedupeKey) {
        // Bulk update per dedupeKey (resolveSystemAlerts).
        if (open) fetchNotifications();
      }
    };
    
    socket.on('notification:new', handleNotificationNew);
    socket.on('notification:count', handleNotificationCount);
    socket.on('notification:read', handleNotificationRead);
    socket.on('notification:state_update', handleStateUpdate);
    
    return () => {
      socket.off('notification:new', handleNotificationNew);
      socket.off('notification:count', handleNotificationCount);
      socket.off('notification:read', handleNotificationRead);
      socket.off('notification:state_update', handleStateUpdate);
    };
  }, [socketContext?.socket, socketContext?.isConnected, open, fetchNotifications]);

  // Self-healing: oltre al safety poll, su ogni (ri)connessione del socket
  // (`socketVersion`++) forziamo un refetch immediato del conteggio (e della
  // lista se il pannello è aperto). Dopo un buco del pub/sub Redis il safety
  // poll a 5min lascerebbe il badge stale troppo a lungo: questo lo riallinea
  // entro pochi secondi dal ripristino, riusando l'endpoint REST esistente.
  const bellSocketVersion = socketContext?.socketVersion ?? 0;
  useEffect(() => {
    if (bellSocketVersion === 0) return;
    fetchUnreadCount();
    if (open) fetchNotifications();
  }, [bellSocketVersion, fetchUnreadCount, fetchNotifications, open]);

  useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open, fetchNotifications]);

  // La barra mobile in basso può chiedere di aprire il pannello notifiche
  // tramite un evento globale (così il singolo `<Popover>` resta unico).
  useEffect(() => {
    const handleOpen = () => setOpen(true);
    const handleToggle = () => setOpen((prev) => !prev);
    window.addEventListener('open-notifications-panel', handleOpen);
    window.addEventListener('toggle-notifications-panel', handleToggle);
    return () => {
      window.removeEventListener('open-notifications-panel', handleOpen);
      window.removeEventListener('toggle-notifications-panel', handleToggle);
    };
  }, []);

  // Ref for stable access to current notifications/token/apiBase
  const notificationsRef = useRef<Notification[]>([]);
  notificationsRef.current = notifications;

  // Auto-remove read notifications after 5 minutes
  useEffect(() => {
    const checkAndRemoveOldReadNotifications = async () => {
      const now = Date.now();
      const currentToken = tokenRef.current;
      const currentNotifications = notificationsRef.current;
      
      if (!currentToken) return;
      
      // Find notifications to remove (read more than 5 minutes ago)
      const idsToRemove: string[] = [];
      currentNotifications.forEach(n => {
        if (n.read && n.read_at) {
          const readTime = new Date(n.read_at).getTime();
          const elapsed = now - readTime;
          if (elapsed >= AUTO_REMOVE_DELAY_MS) {
            idsToRemove.push(n.id);
          }
        }
      });

      if (idsToRemove.length === 0) return;

      // Update state first (remove from UI)
      setNotifications(prev => prev.filter(n => !idsToRemove.includes(n.id)));

      // Then delete from backend (fire and forget, with error handling)
      for (const id of idsToRemove) {
        try {
          await apiClient.delete(`/notifications/${id}`);
        } catch (err) {
          console.error('Error auto-deleting notification:', err);
        }
      }
    };

    // Check every 30 seconds
    const interval = setInterval(checkAndRemoveOldReadNotifications, 30000);
    
    // Initial check after mount
    const timeout = setTimeout(checkAndRemoveOldReadNotifications, 1000);
    
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []); // Empty deps - uses refs for stable access


  const markAsRead = async (id: string) => {
    if (!token) return;
    try {
      await apiClient.put(`/notifications/${id}/read`);
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, read: true, read_at: new Date().toISOString() } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  // Task #3976 — presa in carico manuale di una notifica operativa.
  const acknowledgeNotification = async (id: string) => {
    if (!token) return;
    try {
      const res = await apiClient.patch<NotificationAcknowledgeData>(`/notifications/${id}/acknowledge`, {});
      const acknowledgedAt: string = res.data?.acknowledged_at ?? new Date().toISOString();
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, acknowledged_at: acknowledgedAt } : n)
      );
    } catch (err) {
      console.error('Error acknowledging notification:', err);
    }
  };

  // Task #3976 — risoluzione manuale di una notifica operativa.
  const resolveNotification = async (id: string) => {
    if (!token) return;
    try {
      const res = await apiClient.patch<NotificationResolveData>(`/notifications/${id}/resolve`, {});
      const resolvedAt: string = res.data?.resolved_at ?? new Date().toISOString();
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, resolved_at: resolvedAt } : n)
      );
      // Risolto → esce dal contatore unread se era ancora unread.
      setNotifications(prev => {
        const n = prev.find(x => x.id === id);
        if (n && !n.read && !n.resolved_at) {
          setUnreadCount(c => Math.max(0, c - 1));
        }
        return prev;
      });
    } catch (err) {
      console.error('Error resolving notification:', err);
    }
  };

  const markAllAsRead = async () => {
    if (!token) return;
    try {
      await apiClient.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true, read_at: new Date().toISOString() })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  };

  const deleteNotification = async (id: string) => {
    if (!token) return;
    try {
      await apiClient.delete(`/notifications/${id}`);
      const wasUnread = notifications.find(n => n.id === id && !n.read);
      setNotifications(prev => prev.filter(n => n.id !== id));
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  };

  const formatTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Ora';
    if (diffMins < 60) return `${diffMins}m fa`;
    if (diffHours < 24) return `${diffHours}h fa`;
    if (diffDays < 7) return `${diffDays}g fa`;
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
  };

  return (
    <Popover open={open} onOpenChange={(isOpen) => {
      setOpen(isOpen);
      if (!isOpen) setExpandedId(null);
    }}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative hover:bg-white/50 hover:scale-105 transition-all duration-200 p-2"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs animate-bounce-smooth"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 sm:w-96 p-0" align="end">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20">
          <h3 className="font-semibold text-gray-900 dark:text-white">Notifiche</h3>
          <div className="flex items-center gap-2">
            {desktopNotifs.isSupported && (
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  "text-xs p-1.5",
                  desktopNotifs.isBlocked
                    ? "text-red-600 hover:text-red-700 dark:text-red-400"
                    : desktopNotifs.enabled
                      ? "text-green-600 hover:text-green-700 dark:text-green-400"
                      : "text-gray-400 hover:text-gray-600 dark:text-gray-500"
                )}
                onClick={handleToggleDesktop}
                title={
                  desktopNotifs.isBlocked
                    ? "Notifiche bloccate dal browser — clicca per le istruzioni di sblocco"
                    : desktopNotifs.enabled
                      ? "Notifiche desktop attive"
                      : "Attiva notifiche desktop"
                }
              >
                {desktopNotifs.isBlocked ? (
                  <BellOff className="h-4 w-4" />
                ) : (
                  <BellRing className={cn("h-4 w-4", desktopNotifs.enabled && "animate-pulse")} />
                )}
              </Button>
            )}
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400"
                onClick={markAllAsRead}
              >
                <CheckCheck className="h-4 w-4 mr-1" />
                Segna tutte lette
              </Button>
            )}
          </div>
        </div>

        <ScrollArea className="h-[350px] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-gray-500">
              <Bell className="h-10 w-10 mb-2 opacity-50" />
              <p className="text-sm">Nessuna notifica</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {notifications.map((notification) => {
                // Task #1681 — canale visivo risolto dalla SoT condivisa
                // (channelForNotification): copre messaging, email, calendario e
                // Video Call Live, anche sulle righe storiche prive di
                // `metadata.channel`. Fallback per-tipo quando il tipo non ha
                // un canale dedicato.
                const channelKey = channelForNotification(notification.type, notification.metadata);
                const channelIcon: Record<string, React.ComponentType<{ className?: string }>> = {
                  whatsapp: MessageCircle,
                  sms: MessageSquare,
                  telegram: Send,
                  chat: MessageSquare,
                  email: Mail,
                  feed: Zap,
                  voice: Phone,
                  system: AlertTriangle,
                  calendar: CalendarClock,
                  video: Video,
                };
                const channelColor: Record<string, string> = {
                  whatsapp: 'text-emerald-500',
                  sms: 'text-blue-500',
                  telegram: 'text-sky-500',
                  chat: 'text-indigo-500',
                  email: 'text-orange-500',
                  feed: 'text-purple-500',
                  voice: 'text-green-500',
                  system: 'text-red-500',
                  calendar: 'text-violet-500',
                  video: 'text-red-500',
                };
                const Icon = (channelKey && channelIcon[channelKey]) || typeIcons[notification.type] || Bell;
                const iconColor = (channelKey && channelColor[channelKey]) || typeColors[notification.type] || 'text-gray-500';
                const isExpanded = expandedId === notification.id;
                
                const handleClick = () => {
                  if (!notification.read) {
                    markAsRead(notification.id);
                  }
                  // If the notification has a deep-link, navigate straight to it.
                  if (notification.action_url) {
                    setOpen(false);
                    setExpandedId(null);
                    const target = normalizeActionUrl(notification.action_url, tenantSlug);
                    navigate(target);
                    return;
                  }
                  setExpandedId(isExpanded ? null : notification.id);
                };
                
                const renderMetadataDetails = () => {
                  if (!notification.metadata) return null;
                  const meta = notification.metadata;
                  
                  return (
                    <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 text-xs space-y-1">
                      {meta.caller_number && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Numero:</span>
                          <span className="font-medium text-gray-700 dark:text-gray-300">{meta.caller_number}</span>
                        </div>
                      )}
                      {meta.caller_name && meta.caller_name !== meta.caller_number && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Nome:</span>
                          <span className="font-medium text-gray-700 dark:text-gray-300">{meta.caller_name}</span>
                        </div>
                      )}
                      {meta.destination_number && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Destinazione:</span>
                          <span className="font-medium text-gray-700 dark:text-gray-300">{meta.destination_number}</span>
                        </div>
                      )}
                      {meta.extension_number && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Interno:</span>
                          <span className="font-medium text-gray-700 dark:text-gray-300">{meta.extension_number}</span>
                        </div>
                      )}
                      {meta.duration !== undefined && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Durata:</span>
                          <span className="font-medium text-gray-700 dark:text-gray-300">
                            {meta.duration >= 60 ? `${Math.floor(meta.duration / 60)}m ${meta.duration % 60}s` : `${meta.duration}s`}
                          </span>
                        </div>
                      )}
                      {meta.call_time && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Ora:</span>
                          <span className="font-medium text-gray-700 dark:text-gray-300">
                            {new Date(meta.call_time).toLocaleString('it-IT', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}
                          </span>
                        </div>
                      )}
                      {meta.channel && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Canale:</span>
                          <span className="font-medium text-gray-700 dark:text-gray-300">{meta.channel === 'whatsapp' ? 'WhatsApp' : meta.channel === 'email' ? 'Email' : 'SMS'}</span>
                        </div>
                      )}
                      {meta.from_email && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Email:</span>
                          <span className="font-medium text-gray-700 dark:text-gray-300">{meta.from_name ? `${meta.from_name} (${meta.from_email})` : meta.from_email}</span>
                        </div>
                      )}
                      {meta.subject && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Oggetto:</span>
                          <span className="font-medium text-gray-700 dark:text-gray-300">{meta.subject}</span>
                        </div>
                      )}
                      {meta.from_extension && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Da interno:</span>
                          <span className="font-medium text-gray-700 dark:text-gray-300">{meta.from_extension}</span>
                        </div>
                      )}
                      {meta.to_extension && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">A interno:</span>
                          <span className="font-medium text-gray-700 dark:text-gray-300">{meta.to_extension}</span>
                        </div>
                      )}
                      {meta.transfer_type && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Tipo trasf.:</span>
                          <span className="font-medium text-gray-700 dark:text-gray-300">{meta.transfer_type === 'attended' ? 'Assistito' : 'Diretto'}</span>
                        </div>
                      )}
                      {meta.queue_name && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Coda:</span>
                          <span className="font-medium text-gray-700 dark:text-gray-300">{meta.queue_name}</span>
                        </div>
                      )}
                      {meta.wait_time && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Attesa:</span>
                          <span className="font-medium text-gray-700 dark:text-gray-300">{meta.wait_time}s</span>
                        </div>
                      )}
                      {meta.sender_number && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Da:</span>
                          <span className="font-medium text-gray-700 dark:text-gray-300">{meta.sender_name || meta.sender_number}</span>
                        </div>
                      )}
                      {meta.preview && (
                        <div className="mt-1 p-2 bg-gray-100 dark:bg-gray-800 rounded text-gray-600 dark:text-gray-400 italic">
                          "{meta.preview}"
                        </div>
                      )}
                    </div>
                  );
                };
                
                // Task #3976 — badge stato operativo.
                const opStatus = operationalStatus(notification);
                const statusBadge: Record<string, { label: string; className: string; icon?: React.ComponentType<{ className?: string }> }> = {
                  acknowledged: { label: 'In carico', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: Loader2 },
                  resolved: { label: 'Risolto', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle2 },
                };
                const badge = opStatus && opStatus !== 'unread' && opStatus !== 'read' ? statusBadge[opStatus] : null;
                // Mostra "Prendi in carico" solo se: operativa, non risolta, non già presa in carico.
                const canAcknowledge = opStatus === 'unread' || opStatus === 'read';

                return (
                  <div
                    key={notification.id}
                    className={cn(
                      "p-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer group",
                      !notification.read && !notification.resolved_at && "bg-info/5 dark:bg-info/10",
                      notification.resolved_at && "opacity-75",
                      isExpanded && "bg-gray-50 dark:bg-gray-800/30"
                    )}
                    onClick={handleClick}
                  >
                    <div className="flex gap-3">
                      <div className={cn("flex-shrink-0 mt-0.5", iconColor)}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={cn(
                            "text-sm font-medium",
                            isExpanded ? "" : "truncate",
                            (notification.read || notification.resolved_at) ? "text-muted-foreground" : "text-foreground"
                          )}>
                            {notification.title}
                          </p>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <span className="text-xs text-gray-400">{formatTimeAgo(notification.created_at)}</span>
                            {notification.priority !== 'normal' && !notification.resolved_at && (
                              <Badge className={cn("text-[10px] px-1.5 py-0", priorityStyles[notification.priority])}>
                                {notification.priority === 'urgent' ? '!' : notification.priority === 'high' ? 'H' : 'L'}
                              </Badge>
                            )}
                            {badge && (() => {
                              const BadgeIcon = badge.icon;
                              return (
                                <span className={cn("inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full", badge.className)}>
                                  {BadgeIcon && <BadgeIcon className="h-2.5 w-2.5" />}
                                  {badge.label}
                                </span>
                              );
                            })()}
                          </div>
                        </div>
                        {notification.body && (
                          <p className={cn(
                            "text-xs text-gray-500 dark:text-gray-400 mt-0.5",
                            isExpanded ? "" : "line-clamp-2"
                          )}>
                            {notification.body}
                          </p>
                        )}
                        {isExpanded && renderMetadataDetails()}
                        {/* Task #3976 — azioni operative per notifiche non ancora risolte. */}
                        {isExpanded && opStatus !== null && opStatus !== 'resolved' && (
                          <div className="mt-2 pt-2 border-t border-border flex gap-2 flex-wrap">
                            {canAcknowledge && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 text-[11px] px-2 text-warning border-warning/40 hover:bg-warning/10"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  acknowledgeNotification(notification.id);
                                }}
                              >
                                <Loader2 className="h-3 w-3 mr-1" />
                                Prendi in carico
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-6 text-[11px] px-2 text-success border-success/40 hover:bg-success/10"
                              onClick={(e) => {
                                e.stopPropagation();
                                resolveNotification(notification.id);
                              }}
                            >
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Risolvi
                            </Button>
                          </div>
                        )}
                      </div>
                      <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteNotification(notification.id);
                          }}
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
